import React, { useState, useEffect, useRef } from 'react';
import {
  Briefcase,
  MapPin,
  Calendar,
  Mail,
  Phone,
  ExternalLink,
  Ticket,
  Instagram,
  Linkedin,
  Globe,
  Play,
  TicketIcon
} from 'lucide-react';
import { SocialFeed } from './SocialFeed';
import { messeData } from '../mock';
import { Header } from './Header';
import { Footer } from './Footer';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://nextgenbusinesscup.com/api';

const HERO_SLIDES = [
  '/slideshow/slide1.jpg',
  '/slideshow/slide2.jpg',
  '/slideshow/slide3.jpg',
  '/slideshow/slide4.jpg',
  '/slideshow/slide5.jpg',
  '/slideshow/slide6.jpg',
  '/slideshow/slide7.jpg',
  '/slideshow/slide8.jpg',
  '/slideshow/slide9.jpg',
  '/slideshow/slide10.jpg',
  '/slideshow/slide11.jpg',
  '/slideshow/slide12.jpg',
];

export const Home = () => {
  const heroRef = useRef(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [isPartnerRegisterOpen, setIsPartnerRegisterOpen] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [registerData, setRegisterData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    agb_accepted: false,
  });
  const [companyData, setCompanyData] = useState({
    company_name: '',
    representative_first_name: '',
    representative_last_name: '',
    email: '',
    phone: '',
    password: '',
    agb_accepted: false,
  });
  const [error, setError] = useState('');
  const [ticketInfo, setTicketInfo] = useState(null);
  const [showTicketAuthModal, setShowTicketAuthModal] = useState(false);
  const [afterLoginGoToKaufen, setAfterLoginGoToKaufen] = useState(false);
  const [isFloorplanOpen, setIsFloorplanOpen] = useState(false);
  const [isTurnierplanOpen, setIsTurnierplanOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { login, registerUser, registerCompany, isAuthenticated } = useAuth();
  const { t, locale } = useLanguage();

  const dateDisplay = locale === 'en' ? (messeData.datumEn || 'September 19, 2026') : messeData.datum;
  const ortDisplay = locale === 'en' ? (messeData.ortEn || 'Straßenkickerbase, Cologne') : messeData.ort;

  useEffect(() => {
    const interval = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('login') === 'true') {
      openDialog(setIsLoginOpen);
      navigate('/', { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    const fetchTicketInfo = async () => {
      try {
        const res = await axios.get(`${API_URL}/tickets/info`);
        setTicketInfo(res.data);
      } catch (_) {
        setTicketInfo(null);
      }
    };
    fetchTicketInfo();
  }, []);

  const openDialog = (setDialogOpen) => {
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
    setTimeout(() => {
      setDialogOpen(true);
    }, 10);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ============================================================
          HERO SECTION
      ============================================================ */}
      <section ref={heroRef} id="hero" className="relative w-full min-h-screen overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          {HERO_SLIDES.map((src, i) => (
            <div
              key={src}
              className="absolute inset-0 w-full h-full transition-opacity duration-[1500ms] ease-in-out"
              style={{
                backgroundImage: `url('${src}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                opacity: i === slideIndex ? 1 : 0,
              }}
              aria-hidden={i !== slideIndex}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
        </div>
        <div className="relative z-10 w-full min-h-screen flex flex-col">
          <div className="mt-auto p-6 md:p-10 lg:p-16 max-w-7xl ml-0 lg:ml-8">
            <h1 className="text-7xl sm:text-8xl md:text-9xl lg:text-[11rem] font-bold mb-6 tracking-tight drop-shadow-lg leading-[1] text-left">
              <span className="text-ngbc-accent">Next Gen </span>
              <span className="italic font-normal text-ngbc-mint">Business</span>
              <span className="text-ngbc-accent"> Cup</span>
            </h1>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Calendar className="w-5 h-5 text-ngbc-highlight" />
              <span className="text-white font-semibold">{dateDisplay}</span>
              <span className="text-white/50">|</span>
              <MapPin className="w-5 h-5 text-ngbc-highlight" />
              <a
                href="https://maps.app.goo.gl/DcUvCaokXjJygQvQA"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white font-semibold hover:text-ngbc-mint transition-colors underline underline-offset-2"
              >
                {ortDisplay}
              </a>
            </div>
            <div className="flex flex-row flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4">
              <button
                disabled
                className="shrink-0 bg-gray-500 text-white font-bold text-sm sm:text-base md:text-lg px-5 sm:px-8 md:px-10 py-3.5 sm:py-4 rounded-full transition-all shadow-xl inline-flex items-center justify-center gap-2 sm:gap-3 whitespace-nowrap cursor-not-allowed opacity-70"
              >
                <Ticket className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" />
                {locale === 'en' ? 'BUY TICKET' : 'JETZT TICKET KAUFEN'}
              </button>
              <button
                onClick={() => navigate('/partner')}
                className="shrink-0 bg-white hover:bg-white/90 text-ngbc-dark font-bold text-sm sm:text-base md:text-lg px-5 sm:px-8 md:px-10 py-3.5 sm:py-4 rounded-full transition-all shadow-xl hover:scale-[1.02] inline-flex items-center justify-center gap-2 sm:gap-3 whitespace-nowrap"
              >
                <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" />
                {locale === 'en' ? 'BECOME A PARTNER' : 'PARTNER WERDEN'}
              </button>
            </div>

            {/* Champion Partners — directly under buttons */}
            <div className="mt-6 sm:mt-8 inline-block w-full max-w-[calc(100vw-3rem)] sm:max-w-full bg-black/40 backdrop-blur-sm border border-white/20 rounded-2xl px-5 py-4 sm:px-8 sm:py-5">
              <p className="text-xs sm:text-sm font-bold uppercase tracking-widest text-white/70 mb-3 sm:mb-4">
                {t('home.championPartner')}
              </p>
              <div className="flex flex-row flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 md:gap-6 lg:gap-8">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="w-[calc((100vw-5.5rem)/3)] sm:w-[317px] md:w-[337px] bg-white/15 border border-white/30 rounded-xl flex items-center justify-center hover:bg-white/25 transition-all cursor-pointer shrink-0 py-8">
                    <span className="text-white/80 text-[9px] sm:text-xs font-medium text-center px-2 leading-tight">
                      {locale === 'en' ? 'Soon to be announced' : 'Bald bekannt gegeben'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          WAS IST DER NEXT GEN BUSINESS CUP — two-column layout (design ref)
      ============================================================ */}
      <section id="ueber" className="relative overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
          {/* Left ~33% — dark teal */}
          <div className="bg-ngbc-green text-white px-6 py-12 sm:px-10 sm:py-16 lg:px-12 lg:py-20 flex flex-col">
          <div className="flex flex-col">
            <img
              src="/logo.jpg"
              alt={t('home.logoAlt')}
              className="w-20 h-20 sm:w-24 sm:h-24 object-contain mb-8 sm:mb-10"
              loading="lazy"
            />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold uppercase leading-tight tracking-tight mb-6 sm:mb-8">
              <span className="text-ngbc-accent">Next Gen </span>
              <span className="italic font-normal text-ngbc-mint">Business</span>
              <span className="text-ngbc-accent"> Cup</span>
            </h2>
            <p className="text-base sm:text-lg text-white/90 leading-relaxed">
              {locale === 'en'
                ? 'The Next Gen Business Cup is a unique event format that combines football, careers, and networking. At the Straßenkickerbase, Cologne\'s leading business schools come together to compete in a major football tournament. The tournament forms the core, but the Next Gen Business Cup is much more than just sport. Around the pitch, an interactive event emerges where students and companies come together. While teams compete for the title on the field, new contacts, career opportunities, and inspiring conversations are also being created.'
                : 'Der Next Gen Business Cup ist ein einzigartiges Eventformat, das Fußball, Karriere und Networking miteinander verbindet. In der Straßenkickerbase treffen die größten Wirtschaftshochschulen Kölns aufeinander und treten in einem großen Fußballturnier gegeneinander an. Das Turnier bildet den Kern, doch der Next Gen Business Cup ist deutlich mehr als nur Sport. Rund um das Spielfeld entsteht ein interaktives Event, bei dem Studierende und Unternehmen zusammenkommen. Während auf dem Platz um den Titel gekämpft wird, entstehen gleichzeitig neue Kontakte, Karrierechancen und inspirierende Gespräche.'}
            </p>
            <div className="mt-10 flex flex-col gap-3">
              <button
                type="button"
                disabled
                className="w-full sm:w-auto sm:self-start bg-gray-400 text-white font-bold py-3 px-8 rounded-full text-center shadow-md cursor-not-allowed opacity-70"
              >
                {t('home.ticketsKaufen')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/partner')}
                className="w-full sm:w-auto sm:self-start bg-white/15 hover:bg-white/25 text-white font-bold py-3 px-8 rounded-full transition-all text-center border border-white/35"
              >
                {t('home.partnerWerdenShort')}
              </button>
            </div>
          </div>

          {/* Right ~66% — sage panel */}
          <div className="bg-[#d0e4df] px-5 py-10 sm:px-8 sm:py-12 lg:px-12 lg:py-16 flex flex-col gap-8 lg:gap-10">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {(
                locale === 'en'
                  ? [
                      { title: 'THE TOURNAMENT', body: "Teams from Cologne's leading business schools compete against each other.", image: '/Bild_DasTurnier_compressed.jpg' },
                      { title: 'THE CAREER FAIR', body: 'Companies meet ambitious students.', image: '/Bild_Karrieremesse_compressed.jpg' },
                      { title: 'NEXT GEN STAGE', body: 'Panels, talks and live podcasts on career, leadership and sports business.', image: '/NextGenStage.jpeg' },
                      { title: 'BUSINESS IMPACT ROOMS', body: 'AI-powered CV matching and efficient recruiting conversations.', image: '/Bild_BusinessImpactRooms_compressed.jpg' },
                    ]
                  : [
                      { title: 'DAS TURNIER', body: 'Teams der führenden Wirtschaftshochschulen Kölns treten gegeneinander an', image: '/Bild_DasTurnier_compressed.jpg' },
                      { title: 'DIE KARRIERE-MESSE', body: 'Unternehmen treffen ambitionierte Studierende', image: '/Bild_Karrieremesse_compressed.jpg' },
                      { title: 'NEXT GEN STAGE', body: 'Panels, Talks und Live-Podcasts zu Karriere, Leadership und Sport-Business.', image: '/NextGenStage.jpeg' },
                      { title: 'BUSINESS IMPACT ROOMS', body: 'KI-gestütztes CV-Matching und effiziente Recruiting-Gespräche', image: '/Bild_BusinessImpactRooms_compressed.jpg' },
                    ]
              ).map((pillar, i) => (
                <div key={i} className="flex flex-col">
                  <div className="rounded-2xl lg:rounded-3xl overflow-hidden shadow-xl shadow-ngbc-green/10 bg-white aspect-[3/4] max-h-48 sm:max-h-56 md:max-h-64 lg:max-h-72 relative">
                    <img
                      src={pillar.image}
                      alt={pillar.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-x-0 bottom-0 pb-2 px-2 bg-gradient-to-t from-black/60 to-transparent">
                      <h3 className="text-center text-[10px] sm:text-xs font-bold uppercase tracking-wide text-white drop-shadow-md leading-tight">
                        {pillar.title}
                      </h3>
                    </div>
                  </div>
                  <p className="mt-3 text-xs sm:text-sm text-ngbc-green leading-relaxed font-medium">
                    {pillar.body}
                  </p>
                </div>
              ))}
            </div>

            <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl shadow-ngbc-green/15 max-h-[min(420px,50vh)] flex items-center justify-center border border-white/40 bg-black aspect-video w-full">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/PVj_fs_6anE"
                title="Next Gen Business Cup Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          ABLAUF / PROGRAM
          Light background
      ============================================================ */}
      <section id="ablauf" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#e9e3db]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-ngbc-green mb-3">{t('home.ablauf')}</h2>
            <p className="text-base text-gray-500">{t('home.ablaufSub')}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            
            {/* Next Gen Stage Programm */}
            <div className="bg-white rounded-xl overflow-hidden shadow-md">
              <div className="w-full flex items-center p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-ngbc-mint/20 flex items-center justify-center">
                    <Play className="w-6 h-6 text-ngbc-green" />
                  </div>
                  <h3 className="text-lg font-bold text-ngbc-green pr-4">{t('home.nextGenStage')}</h3>
                </div>
              </div>
              <div className="px-6 pb-6">
                <div className="bg-ngbc-mint/10 rounded-xl p-5 border-l-4 border-ngbc-mint">
                  <p className="text-gray-700 text-sm leading-relaxed">{t('home.freutEuchProgramm')}</p>
                </div>
              </div>
            </div>

            {/* Digitaler Turnierplan */}
            <div 
              className="bg-white rounded-xl overflow-hidden shadow-md cursor-pointer group hover:shadow-lg transition-shadow"
              onClick={() => setIsTurnierplanOpen(true)}
              onKeyDown={(e) => e.key === 'Enter' && setIsTurnierplanOpen(true)}
              role="button"
              tabIndex={0}
              aria-label={t('home.turnierplanEnlarge')}
            >
              <div className="w-full flex items-center p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-ngbc-mint/20 flex items-center justify-center">
                    <TicketIcon className="w-6 h-6 text-ngbc-green" />
                  </div>
                  <h3 className="text-lg font-bold text-ngbc-green pr-4">{t('home.turnierplan')}</h3>
                </div>
              </div>
              <div className="px-6 pb-6">
                <div className="bg-ngbc-mint/10 rounded-xl p-3 border-l-4 border-ngbc-mint">
                  <img src="/Playplan.jpeg" alt="Turnierplan" className="w-full h-auto rounded-lg" />
                </div>
              </div>
              <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                {t('home.turnierplanEnlarge')}
              </div>
            </div>

          </div>
        </div>
      </section>


      {/* ============================================================
          FLOORPLAN — contained, clickable card
      ============================================================ */}
      <section id="floorplan" className="py-14 px-4 sm:px-6 lg:px-8 bg-[#F9F7F0]">
        <div className="max-w-5xl mx-auto">
          <div
            className="relative rounded-3xl overflow-hidden shadow-xl border border-ngbc-mint/30 bg-white cursor-pointer group"
            onClick={() => setIsFloorplanOpen(true)}
            onKeyDown={(e) => e.key === 'Enter' && setIsFloorplanOpen(true)}
            role="button"
            tabIndex={0}
            aria-label={t('home.floorplanEnlarge')}
          >
            <img
              src="/lagePlan.jpeg"
              alt={t('home.floorplanAlt')}
              className="w-full h-auto max-h-[min(65vh,700px)] object-contain transition-opacity group-hover:opacity-90"
              loading="lazy"
            />
            <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              {t('home.floorplanEnlarge')}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          SOCIAL WALL — collage + feed cards (design ref)
      ============================================================ */}
      <section id="social-wall" className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-[#006666]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left: photo collage with images */}
          <div className="relative min-h-[320px] lg:min-h-[480px]">
            <div className="grid grid-cols-12 gap-2 sm:gap-3 h-full auto-rows-[minmax(72px,1fr)]">
              <div className="col-span-7 row-span-3 rounded-2xl overflow-hidden">
                <img src="/Bild1.png" alt="Social wall photo 1" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="col-span-5 row-span-2 rounded-2xl overflow-hidden">
                <img src="/Bild2.png" alt="Social wall photo 2" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="col-span-5 row-span-2 rounded-2xl overflow-hidden">
                <img src="/Bild3.png" alt="Social wall photo 3" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="col-span-4 row-span-2 rounded-2xl overflow-hidden">
                <img src="/Bild4.png" alt="Social wall photo 4" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="col-span-8 row-span-2 rounded-2xl overflow-hidden">
                <img src="/Bild5.png" alt="Social wall photo 5" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>

          {/* Right: title + platform cards */}
          <div>
            <h2 className="text-3xl md:text-5xl font-bold text-white text-right mb-8 md:mb-10">{t('home.socialWall')}</h2>

            <div className="flex flex-col gap-5">
              {/* Social Feed - Fetches latest posts from Instagram & LinkedIn */}
              <SocialFeed />
            </div>

            <div className="flex flex-wrap justify-end gap-2 mt-8 pt-6 border-t border-white/15">
              {messeData.socialMedia.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full text-white text-xs sm:text-sm font-medium transition-all border border-white/15"
                >
                  <Globe className="w-3.5 h-3.5 text-white/70" />
                  {social.platform}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* ============================================================
          KONTAKT SECTION
          Light background
      ============================================================ */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-ngbc-green mb-3">{t('home.contactTitle')}</h2>
            <p className="text-base text-gray-500">{t('home.contactSub')}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto mb-8">
            
            {/* Email */}
            <div className="bg-ngbc-mint/10 rounded-xl p-5 flex items-start gap-4 border border-ngbc-mint/20 min-w-0">
              <div className="w-12 h-12 bg-ngbc-mint rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-ngbc-green text-sm mb-1">{t('home.emailLabel')}</p>
                <a href={`mailto:${messeData.kontakt.email}`} className="text-ngbc-green hover:text-ngbc-accent text-sm font-medium transition-colors break-all">
                  {messeData.kontakt.email}
                </a>
              </div>
            </div>

            {/* Phone */}
            <div className="bg-ngbc-green/10 rounded-xl p-5 flex items-start gap-4 border border-ngbc-green/20">
              <div className="w-12 h-12 bg-ngbc-green rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-ngbc-green text-sm mb-1">{t('home.telefon')}</p>
                <a href={`tel:${messeData.kontakt.telefon}`} className="text-ngbc-green hover:text-ngbc-accent text-sm font-medium transition-colors">
                  {messeData.kontakt.telefon}
                </a>
              </div>
            </div>

            {/* Location */}
            <a
              href="https://maps.app.goo.gl/DcUvCaokXjJygQvQA"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-ngbc-mint/10 rounded-xl p-5 flex items-start gap-4 border border-ngbc-mint/20 sm:col-span-2 lg:col-span-1 hover:bg-ngbc-mint/20 transition-colors group"
            >
              <div className="w-12 h-12 bg-ngbc-mint rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-ngbc-green text-sm mb-1">{t('home.adresse')}</p>
                <p className="text-gray-500 text-sm">{messeData.kontakt.adresse}</p>
                <p className="text-ngbc-highlight text-xs mt-1 group-hover:text-ngbc-accent transition-colors">
                  {locale === 'en' ? 'Open in Maps →' : 'In Maps öffnen →'}
                </p>
              </div>
            </a>

          </div>

          {/* Map */}
          <div className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100 max-w-4xl mx-auto">
            <a
              href="https://maps.app.goo.gl/DcUvCaokXjJygQvQA"
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-xl overflow-hidden border-2 border-ngbc-mint/30 hover:border-ngbc-highlight transition-colors"
              title={t('home.eventLocation')}
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2509.4567!2d7.0!3d50.96!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47bee1c4a1c70f49%3A0x41575e576177dfa1!2sSchanzenstra%C3%9Fe%206-20%2C%2051063%20K%C3%B6ln!5e0!3m2!1sen!2sde!4v1709222222222!5m2!1sen!2sde"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={t('home.eventLocation')}
              ></iframe>
            </a>
            <div className="p-3 bg-ngbc-mint/10 text-center">
              <a
                href="https://maps.app.goo.gl/DcUvCaokXjJygQvQA"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ngbc-green hover:text-ngbc-accent text-sm font-medium transition-colors inline-flex items-center gap-1"
              >
                <MapPin className="w-4 h-4" />
                {locale === 'en' ? 'Open in Google Maps' : 'In Google Maps öffnen'}
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* ============================================================
          MODALS
      ============================================================ */}

      {/* Floorplan Fullscreen Modal */}
      {isFloorplanOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 sm:p-6 md:p-8"
          onClick={(e) => e.target === e.currentTarget && setIsFloorplanOpen(false)}
        >
          <div className="relative w-full h-full flex flex-col">
            <div className="flex items-center justify-between mb-3 shrink-0">
              <h3 className="text-white font-bold text-lg">{t('home.floorplan')}</h3>
              <button
                onClick={() => setIsFloorplanOpen(false)}
                className="rounded-full bg-white/20 hover:bg-white/30 text-white px-4 py-2 text-sm font-semibold transition-colors border border-white/25"
              >
                {t('home.close')}
              </button>
            </div>
            <div className="flex-1 min-h-0 rounded-2xl overflow-hidden border border-white/15 shadow-2xl">
              <img
                src="/lagePlan.jpeg"
                alt={t('home.floorplanAlt')}
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      )}

      {/* Turnierplan Fullscreen Modal */}
      {isTurnierplanOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 sm:p-6 md:p-8"
          onClick={(e) => e.target === e.currentTarget && setIsTurnierplanOpen(false)}
        >
          <div className="relative w-full h-full flex flex-col">
            <div className="flex items-center justify-between mb-3 shrink-0">
              <h3 className="text-white font-bold text-lg">{t('home.turnierplan')}</h3>
              <button
                onClick={() => setIsTurnierplanOpen(false)}
                className="rounded-full bg-white/20 hover:bg-white/30 text-white px-4 py-2 text-sm font-semibold transition-colors border border-white/25"
              >
                {t('home.close')}
              </button>
            </div>
            <div className="flex-1 min-h-0 rounded-2xl overflow-hidden border border-white/15 shadow-2xl bg-white">
              <img
                src="/Playplan.jpeg"
                alt="Turnierplan"
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      )}

      {/* Ticket Auth Modal */}
      <Dialog open={showTicketAuthModal} onOpenChange={setShowTicketAuthModal}>
        <DialogContent className="sm:max-w-md bg-[#F7F3EB] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-ngbc-green">{t('home.ticketDialogTitle')}</DialogTitle>
            <DialogDescription className="text-gray-500">{t('home.ticketDialogDesc')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-4">
            <Button
              className="w-full bg-ngbc-highlight hover:bg-ngbc-accent text-white font-bold py-3 rounded-full transition-colors shadow-sm"
              onClick={() => { setShowTicketAuthModal(false); setAfterLoginGoToKaufen(true); openDialog(setIsLoginOpen); }}
            >
              {t('home.login')}
            </Button>
            <Button
              variant="outline"
              className="w-full border-2 border-ngbc-green text-ngbc-green hover:bg-ngbc-green hover:text-white font-bold py-3 rounded-full transition-colors"
              onClick={() => { setShowTicketAuthModal(false); setAfterLoginGoToKaufen(true); openDialog(setIsRegisterOpen); }}
            >
              {t('home.register')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Login Dialog */}
      <Dialog open={isLoginOpen} onOpenChange={setIsLoginOpen}>
        <DialogContent className="sm:max-w-md bg-[#F7F3EB] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-ngbc-green">{t('home.loginTitle')}</DialogTitle>
            <DialogDescription className="text-gray-500">{t('home.loginDesc')}</DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4 mt-4"
            onSubmit={async (e) => {
              e.preventDefault();
              setError('');
              const result = await login(loginEmail, loginPassword);
              if (result.success) {
                setIsLoginOpen(false);
                navigate(afterLoginGoToKaufen ? '/kaufen' : (location.state?.from?.pathname || '/'), { replace: true });
                setAfterLoginGoToKaufen(false);
              } else {
                setError(result.error);
              }
            }}
          >
            {error && <div className="text-red-600 text-sm bg-red-50 rounded-lg p-3">{error}</div>}
            <div>
              <Label htmlFor="login-email" className="text-gray-700">{t('home.email')}</Label>
              <Input
                id="login-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className="rounded-lg border-gray-200 bg-white text-gray-800 focus:ring-ngbc-mint"
              />
            </div>
            <div>
              <Label htmlFor="login-password" className="text-gray-700">{t('home.passwort')}</Label>
              <Input
                id="login-password"
                name="password"
                type="password"
                required
                autoComplete="current-password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="rounded-lg border-gray-200 bg-white text-gray-800 focus:ring-ngbc-mint"
              />
            </div>
            <Button type="submit" className="w-full bg-ngbc-highlight hover:bg-ngbc-accent text-white font-bold py-3 rounded-full transition-colors shadow-sm">
              {t('home.login')}
            </Button>
            <p className="text-sm text-center text-gray-500">
              {t('home.noAccount')}{' '}
              <button type="button" onClick={() => { setIsLoginOpen(false); openDialog(setIsRegisterOpen); }} className="text-ngbc-green hover:text-ngbc-accent font-semibold transition-colors">
                {t('home.jetztRegistrieren')}
              </button>
            </p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Register Dialog */}
      <Dialog open={isRegisterOpen} onOpenChange={setIsRegisterOpen}>
        <DialogContent className="sm:max-w-md bg-[#F7F3EB] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-ngbc-green">{t('home.registerTitle')}</DialogTitle>
            <DialogDescription className="text-gray-500">{t('home.registerDesc')}</DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4 mt-4"
            onSubmit={async (e) => {
              e.preventDefault();
              setError('');
              if (!registerData.agb_accepted) { setError(t('home.agbAkzeptieren')); return; }
              const result = await registerUser(registerData);
              if (result.success) {
                setIsRegisterOpen(false);
                navigate(afterLoginGoToKaufen ? '/kaufen' : (location.state?.from?.pathname || '/'), { replace: true });
                setAfterLoginGoToKaufen(false);
              } else { setError(result.error); }
            }}
          >
            {error && <div className="text-red-600 text-sm bg-red-50 rounded-lg p-3">{error}</div>}
            <div>
              <Label htmlFor="register-first-name" className="text-gray-700">{t('home.vorname')}</Label>
              <input
                id="register-first-name"
                name="first_name"
                type="text"
                required
                autoComplete="given-name"
                className="flex h-10 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ngbc-mint focus:border-transparent"
                value={registerData.first_name}
                onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="register-last-name" className="text-gray-700">{t('home.nachname')}</Label>
              <input
                id="register-last-name"
                name="last_name"
                type="text"
                required
                autoComplete="family-name"
                className="flex h-10 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ngbc-mint focus:border-transparent"
                value={registerData.last_name}
                onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="register-email" className="text-gray-700">{t('home.email')}</Label>
              <input
                id="register-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                className="flex h-10 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ngbc-mint focus:border-transparent"
                value={registerData.email}
                onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="register-password" className="text-gray-700">{t('home.passwort')}</Label>
              <input
                id="register-password"
                name="password"
                type="password"
                required
                autoComplete="new-password"
                className="flex h-10 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ngbc-mint focus:border-transparent"
                value={registerData.password}
                onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
              />
            </div>
            <div className="flex items-start space-x-2">
              <Checkbox
                id="agb-user"
                name="agb_accepted"
                checked={registerData.agb_accepted}
                onCheckedChange={(checked) => setRegisterData({ ...registerData, agb_accepted: checked })}
              />
              <Label htmlFor="agb-user" className="text-sm text-gray-600 cursor-pointer leading-relaxed">
                {t('home.agbLabel')}{' '}
                <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-ngbc-green hover:text-ngbc-accent underline transition-colors">{t('home.agb')}</a>
                {' '}{t('home.und')}{' '}
                <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-ngbc-green hover:text-ngbc-accent underline transition-colors">{t('home.datenschutzerklaerung')}</a>
              </Label>
            </div>
            <Button type="submit" className="w-full bg-ngbc-highlight hover:bg-ngbc-accent text-white font-bold py-3 rounded-full transition-colors shadow-sm">
              {t('home.register')}
            </Button>
            <p className="text-sm text-center text-gray-500">
              {t('home.bereitsKonto')}{' '}
              <button type="button" onClick={() => { setIsRegisterOpen(false); openDialog(setIsLoginOpen); }} className="text-ngbc-green hover:text-ngbc-accent font-semibold transition-colors">
                {t('home.jetztAnmelden')}
              </button>
            </p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Partner Register Dialog */}
      <Dialog open={isPartnerRegisterOpen} onOpenChange={setIsPartnerRegisterOpen}>
        <DialogContent className="sm:max-w-md bg-[#F7F3EB] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-ngbc-green">{t('home.partnerRegisterTitle')}</DialogTitle>
            <DialogDescription className="text-gray-500">{t('home.partnerRegisterDesc')}</DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4 mt-4"
            onSubmit={async (e) => {
              e.preventDefault();
              setError('');
              if (!companyData.agb_accepted) { setError(t('home.agbAkzeptieren')); return; }
              const result = await registerCompany(companyData);
              if (result.success) {
                setIsPartnerRegisterOpen(false);
                navigate(location.state?.from?.pathname || '/', { replace: true });
              } else { setError(result.error); }
            }}
          >
            {error && <div className="text-red-600 text-sm bg-red-50 rounded-lg p-3">{error}</div>}
            <div>
              <Label htmlFor="company-name" className="text-gray-700">{t('home.firmenname')}</Label>
              <Input
                id="company-name"
                name="company_name"
                type="text"
                required
                autoComplete="organization"
                value={companyData.company_name}
                onChange={(e) => setCompanyData({ ...companyData, company_name: e.target.value })}
                className="rounded-lg border-gray-200 bg-white text-gray-800"
              />
            </div>
            <div>
              <Label htmlFor="rep-first-name" className="text-gray-700">{t('home.vornameVertreter')}</Label>
              <Input
                id="rep-first-name"
                name="representative_first_name"
                type="text"
                required
                autoComplete="given-name"
                value={companyData.representative_first_name}
                onChange={(e) => setCompanyData({ ...companyData, representative_first_name: e.target.value })}
                className="rounded-lg border-gray-200 bg-white text-gray-800"
              />
            </div>
            <div>
              <Label htmlFor="rep-last-name" className="text-gray-700">{t('home.nachnameVertreter')}</Label>
              <Input
                id="rep-last-name"
                name="representative_last_name"
                type="text"
                required
                autoComplete="family-name"
                value={companyData.representative_last_name}
                onChange={(e) => setCompanyData({ ...companyData, representative_last_name: e.target.value })}
                className="rounded-lg border-gray-200 bg-white text-gray-800"
              />
            </div>
            <div>
              <Label htmlFor="company-email" className="text-gray-700">{t('home.email')} *</Label>
              <Input
                id="company-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                value={companyData.email}
                onChange={(e) => setCompanyData({ ...companyData, email: e.target.value })}
                className="rounded-lg border-gray-200 bg-white text-gray-800"
              />
            </div>
            <div>
              <Label htmlFor="company-phone" className="text-gray-700">{t('home.telefonOptionalLabel')}</Label>
              <Input
                id="company-phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                value={companyData.phone}
                onChange={(e) => setCompanyData({ ...companyData, phone: e.target.value })}
                className="rounded-lg border-gray-200 bg-white text-gray-800"
              />
            </div>
            <div>
              <Label htmlFor="company-password" className="text-gray-700">{t('home.passwortLabel')}</Label>
              <Input
                id="company-password"
                name="password"
                type="password"
                required
                autoComplete="new-password"
                value={companyData.password}
                onChange={(e) => setCompanyData({ ...companyData, password: e.target.value })}
                className="rounded-lg border-gray-200 bg-white text-gray-800"
              />
            </div>
            <div className="flex items-start space-x-2">
              <Checkbox
                id="agb-company"
                name="agb_accepted"
                checked={companyData.agb_accepted}
                onCheckedChange={(checked) => setCompanyData({ ...companyData, agb_accepted: checked })}
              />
              <Label htmlFor="agb-company" className="text-sm text-gray-600 cursor-pointer leading-relaxed">
                {t('home.agbLabel')}{' '}
                <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-ngbc-green hover:text-ngbc-accent underline transition-colors">{t('home.agb')}</a>
                {' '}{t('home.und')}{' '}
                <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-ngbc-green hover:text-ngbc-accent underline transition-colors">{t('home.datenschutzerklaerung')}</a>
              </Label>
            </div>
            <Button type="submit" className="w-full bg-ngbc-highlight hover:bg-ngbc-accent text-white font-bold py-3 rounded-full transition-colors shadow-sm">
              {t('home.alsPartnerRegistrieren')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
